'use client';

import { useDynamicContext } from '@dynamic-labs/sdk-react-core';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import React from 'react';

import { Metadata } from '@repo/types-client';
import { Icon, tw, UserPFP } from '@repo/ui';
import { useScreenSize } from '@repo/utils';

type Sublink = {
  name: string;
  onClick: () => void;
  icon: string;
  folder?: string;
  hide?: boolean;
};

export const UserDropdown: React.FC = () => {
  const { isMobile } = useScreenSize();
  const { user, setShowDynamicUserProfile, handleLogOut } = useDynamicContext();

  const sublinks: Sublink[][] = [
    [
      {
        name: 'Wallets',
        hide: false,
        onClick: () => setShowDynamicUserProfile(true),
        icon: 'wallets',
      },
    ],
  ];

  return (
    <Popover className='flex justify-center'>
      {() => (
        <>
          <PopoverButton className={'focus:outline-none'}>
            <div
              className={tw(
                'flex gap-2 bg-white bg-opacity-[.08]',
                'items-center justify-center rounded-full p-1',
              )}
            >
              <div
                className={tw(
                  'relative flex h-6 w-6 overflow-hidden rounded-full',
                  'bg-[#F2F2F2] md:h-7 md:w-7',
                )}
              >
                <UserPFP
                  publicUrl={(user?.metadata as Metadata)?.pfp?.url}
                  type={(user?.metadata as Metadata)?.pfp?.type}
                />
              </div>
              <ChevronDownIcon
                fill='white'
                className='h-4 w-4'
              />
            </div>
          </PopoverButton>
          <PopoverPanel
            anchor='bottom end'
            className='z-[60] mt-4 h-[75px] rounded-2xl md:h-auto'
          >
            <div
              className={tw(
                'flex h-full flex-col overflow-hidden rounded-l-2xl',
                'w-[110px] bg-[#181818] text-white md:w-[130px]',
              )}
            >
              {sublinks?.map((section: Sublink[], sectionIndex: number) => (
                <div
                  key={sectionIndex}
                  className='w-full md:border-b-2 md:border-[#2e2e2e]'
                >
                  {section.map((sublink: Sublink, index: number) => (
                    <div key={index}>
                      {!sublink.hide ? (
                        <div
                          onClick={() => sublink.onClick()}
                          className={tw(
                            'flex w-full items-center gap-3 p-2 hover:cursor-pointer',
                            'md:px-4 md:py-3 dark:hover:bg-[#2e2e2e]',
                          )}
                        >
                          {sublink.icon ? (
                            <Icon
                              height={isMobile ? 20 : 22}
                              width={isMobile ? 20 : 22}
                              name={sublink.icon}
                              folder={sublink.folder || 'core'}
                            />
                          ) : null}
                          <p
                            className={tw(
                              'font-fredoka text-xs font-normal text-white md:text-sm',
                            )}
                          >
                            {sublink.name}
                          </p>
                        </div>
                      ) : null}
                    </div>
                  ))}
                </div>
              ))}
              <div
                onClick={() => {
                  handleLogOut();
                }}
                className={tw(
                  'absolute bottom-0 left-0 flex w-full cursor-pointer items-center',
                  'gap-3 border-t-2 border-[#2e2e2e] p-2 md:px-4 md:py-3',
                  'md:relative md:border-t-0 dark:hover:bg-[#2e2e2e]',
                )}
              >
                <Icon
                  height={isMobile ? 18 : 20}
                  width={isMobile ? 18 : 20}
                  name={'sign-out'}
                  folder={'core'}
                  className='pl-0.5'
                />
                <p
                  className={tw(
                    'font-fredoka text-xs font-normal text-white md:text-sm',
                  )}
                >
                  Sign Out
                </p>
              </div>
            </div>
          </PopoverPanel>
        </>
      )}
    </Popover>
  );
};
